import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createReactionEngine } from '../data/labCatalog';
import SoundPlayer from '../models/SoundPlayer';
import { MAX_SELECTED_SUBSTANCES, REACTION_ANIMATION_MS } from '../constants/config';
import { UI_SOUNDS } from '../constants/effects';
import { MIX_PHASES } from '../constants/labPhases';
import { toggleItemWithLimit } from '../utils/arrayUtils';

/**
 * Lab state: which substances are picked, mixing them, and handing the
 * result to the assistant once the animation and sound have played.
 */
export default function useMixingLab({ assistant, showMessage }, initialPickedIds = []) {
  const reactionEngine = useMemo(() => createReactionEngine(), []);
  const soundPlayer = useMemo(() => new SoundPlayer(), []);

  // Links like "/?pick=K,H2O" (from the element and reaction pages) start with those picked.
  const [selectedIds, setSelectedIds] = useState(() =>
    [...new Set(initialPickedIds)].filter((id) => reactionEngine.getSubstance(id)).slice(0, MAX_SELECTED_SUBSTANCES)
  );
  const [currentReaction, setCurrentReaction] = useState(null);
  const [mixPhase, setMixPhase] = useState(MIX_PHASES.IDLE);
  const [mixCount, setMixCount] = useState(0);
  const explainTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(explainTimerRef.current), []);

  const selectedSubstances = selectedIds.map((id) => reactionEngine.getSubstance(id));
  const canMix = selectedIds.length === MAX_SELECTED_SUBSTANCES && mixPhase !== MIX_PHASES.PLAYING;

  const toggleSubstance = useCallback(
    (substanceId) => {
      const nextSelectedIds = toggleItemWithLimit(selectedIds, substanceId, MAX_SELECTED_SUBSTANCES);
      setSelectedIds(nextSelectedIds);
      if (nextSelectedIds.includes(substanceId)) {
        soundPlayer.play(UI_SOUNDS.SELECT, 'select');
        showMessage(assistant.createSelectionMessage(reactionEngine.getSubstance(substanceId), nextSelectedIds.length));
      }
    },
    [selectedIds, soundPlayer, showMessage, assistant, reactionEngine]
  );

  const mixSelectedSubstances = useCallback(() => {
    if (!canMix) return;
    const reaction = reactionEngine.mix(...selectedIds);
    setCurrentReaction(reaction);
    setMixCount((count) => count + 1);
    setMixPhase(MIX_PHASES.PLAYING);
    soundPlayer.play(reaction.soundPath, reaction.effect);
    showMessage(assistant.createThinkingMessage());

    clearTimeout(explainTimerRef.current);
    explainTimerRef.current = setTimeout(() => {
      setMixPhase(MIX_PHASES.EXPLAINED);
      showMessage(assistant.createExplanation(reaction));
    }, REACTION_ANIMATION_MS);
  }, [canMix, reactionEngine, selectedIds, soundPlayer, showMessage, assistant]);

  const resetBench = useCallback(() => {
    clearTimeout(explainTimerRef.current);
    setSelectedIds([]);
    setCurrentReaction(null);
    setMixPhase(MIX_PHASES.IDLE);
  }, []);

  return {
    allSubstances: reactionEngine.getAllSubstances(),
    selectedIds,
    selectedSubstances,
    currentReaction,
    mixPhase,
    mixCount,
    canMix,
    toggleSubstance,
    mixSelectedSubstances,
    resetBench,
  };
}
